import { useScienceTutorSection } from './scienceTutorContext';
import { Pause, Play } from 'lucide-react';
import { useMemo, useState } from 'react';

import { formatSig, type GasMode } from './gasLaws';
import { ExploreChallenges } from './ExploreChallenges';
import {
  DEFAULT_GAS_EXPLORE,
  GAS_EXPLORE_CHALLENGES,
  GAS_EXPLORE_LIMITS,
  GAS_EXPLORE_PRESETS,
  GAS_EXPLORE_QUESTIONS,
  IDEAL_UNKNOWNS,
  calculateGasExplore,
  changeGasUnit,
  changeGasUnknown,
  gasExploreFields,
  gasExploreInputs,
  gasExploreSliderKey,
  slotSymbol,
  type GasExploreResult,
  type GasExploreSetup,
  type GasExploreSlot,
  type GasExploreVariable,
} from './gasLawsExplore';
import { GasLawsSyringe3D } from './GasSyringe3D';
import { GasGraph, ParticleBox } from './GasLawsVisuals';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

const UNIT_OPTIONS = {
  p: [
    { value: 'kPa', label: 'kPa' },
    { value: 'atm', label: 'atm' },
  ],
  V: [
    { value: 'L', label: 'L' },
    { value: 'mL', label: 'mL' },
  ],
  T: [
    { value: 'K', label: 'K' },
    { value: 'C', label: '°C' },
  ],
} as const;

const UNIT_NAMES = { p: 'Pressure unit', V: 'Volume unit', T: 'Temperature unit' } as const;

const FIXED_COPY: Record<GasMode, string> = {
  boyle: 'Fixed: temperature 298.15 K (25 °C) and the amount of gas. Type the starting gas and the new volume.',
  charles: 'Fixed: pressure 100 kPa and the amount of gas. The plunger moves freely, so the volume follows the temperature.',
  ideal: 'Choose the quantity to find, then type the other three. The syringe is held at the volume that pV = nRT gives.',
};

function compute(setup: GasExploreSetup): { result: GasExploreResult | null; error: string | null } {
  try {
    return { result: calculateGasExplore(setup), error: null };
  } catch (caught) {
    return { result: null, error: caught instanceof Error ? caught.message : 'This setup cannot be solved.' };
  }
}

/**
 * Explore view of the Gas Laws Lab: the learner's typed p, V, n and T drive the worked
 * calculation, the 3D gas syringe, the particle box, the graph and the readouts from one state.
 */
export function GasLawsExploreLab({ mode }: { mode: GasMode }) {
  const reducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState(true);
  const [setup, setSetup] = useState<GasExploreSetup>(DEFAULT_GAS_EXPLORE[mode]);
  useScienceTutorSection('GasLawsExploreLab', { mode, setup });
  const { result, error } = useMemo(() => compute(setup), [setup]);
  const [lastInModel, setLastInModel] = useState<GasExploreResult>(() => calculateGasExplore(DEFAULT_GAS_EXPLORE[mode]));
  const shown = result && result.withinModel ? result : lastInModel;
  const moving = playing && !reducedMotion;

  const apply = (next: GasExploreSetup) => {
    setSetup(next);
    const computed = compute(next).result;
    if (computed && computed.withinModel) setLastInModel(computed);
  };

  const fields = gasExploreFields(setup);
  const inputs = gasExploreInputs(setup);
  const values = Object.fromEntries(inputs.map((slot) => [slot, setup.values[slot] ?? Number.NaN])) as Record<GasExploreSlot, number>;
  const unitVariables = (['p', 'V', 'T'] as const).filter((variable) =>
    inputs.some((slot) => slot[0] === variable) || (setup.unknown as string)[0] === variable,
  );
  const panelKey = `${setup.mode}-${setup.unknown}-${setup.units.p}-${setup.units.V}-${setup.units.T}`;
  const { state } = shown;
  const pressureKPa = shown.baseValues.p;

  return (
    <section
      aria-label="Gas law explore"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Explore</p>
          <h2 className="mt-1 text-xl font-extrabold text-ink">Type your own gas and watch every view change</h2>
          <p className="mt-1 max-w-2xl text-sm font-medium leading-6 text-slate2">{FIXED_COPY[mode]}</p>
        </div>
        <button
          type="button"
          onClick={() => setPlaying((current) => !current)}
          disabled={reducedMotion}
          className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-4 py-2 text-sm font-extrabold text-ink-soft disabled:opacity-50"
        >
          {moving ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
          {reducedMotion ? 'Motion reduced' : moving ? 'Pause motion' : 'Play motion'}
        </button>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          {mode === 'ideal' ? (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Quantity to find">
              {IDEAL_UNKNOWNS.map((variable) => (
                <button
                  key={variable}
                  type="button"
                  aria-pressed={setup.unknown === variable}
                  onClick={() => apply(changeGasUnknown(setup, variable as GasExploreVariable, result))}
                  className={`rounded-full border px-4 py-2 text-sm font-extrabold ${
                    setup.unknown === variable ? 'border-brand-mint bg-wash-mint text-ink' : 'border-hairline bg-white text-slate2'
                  }`}
                >
                  Find {variable}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {unitVariables.map((variable) => (
              <label key={variable} className="flex items-center gap-2 text-sm font-extrabold text-ink">
                {UNIT_NAMES[variable]}
                <select
                  value={setup.units[variable]}
                  onChange={(event) => apply(changeGasUnit(setup, variable, event.target.value))}
                  className="rounded-xl border border-hairline bg-white px-2 py-1.5 font-bold text-ink"
                >
                  {UNIT_OPTIONS[variable].map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <ModelInputPanel
            key={panelKey}
            legend={`Your gas · find ${slotSymbol(setup.unknown)}`}
            fields={fields}
            values={values}
            onChange={(next) => apply({ ...setup, values: { ...setup.values, ...next } })}
            sliderKey={gasExploreSliderKey(setup)}
          />
          <div className="flex flex-wrap gap-2" aria-label="Gas presets">
            {GAS_EXPLORE_PRESETS[mode].map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => apply(preset.setup)}
                className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <ExploreChallenges question={GAS_EXPLORE_QUESTIONS[mode]} challenges={GAS_EXPLORE_CHALLENGES[mode]} result={result} />
        </div>
        <div className="min-w-0 space-y-4">
          <figure className="min-w-0">
            <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">What you see</figcaption>
            <GasLawsSyringe3D
              state={{ volumeL: state.volumeL, capacityL: GAS_EXPLORE_LIMITS.V.max, pressureKPa, temperatureK: state.temperatureK }}
              playing={moving}
              label={`Sealed gas syringe holding ${formatSig(state.volumeL, 3)} L of gas at ${formatSig(pressureKPa, 3)} kPa and ${formatSig(state.temperatureK, 4)} K`}
            />
            <p className="mt-2 text-xs font-medium text-slate2">
              Syringe drawn to hold {GAS_EXPLORE_LIMITS.V.max} L; a real gas syringe holds about 100 mL. Gauge reads 0–1000 kPa. Not to scale.
            </p>
          </figure>
          <figure className="min-w-0">
            <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">Zoomed in: gas particles</figcaption>
            <ParticleBox mode={mode} state={state} running={moving} maxVolumeL={GAS_EXPLORE_LIMITS.V.max} />
          </figure>
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0 rounded-[1.5rem] border border-hairline bg-surface p-3">
          <GasGraph
            mode={mode}
            state={state}
            revealed
            celsius={false}
            axis={{ volumeMaxL: GAS_EXPLORE_LIMITS.V.max, temperatureMaxK: GAS_EXPLORE_LIMITS.T.max }}
          />
        </div>
        <div className="min-w-0">
          {result ? (
            <WorkedStepsPanel
              title="How the model works it out"
              steps={result.steps}
              stepTestIdPrefix="gas-step"
              result={result.result}
              resultTestId="gas-result"
              limitNote={result.limitNote}
            />
          ) : (
            <p role="alert" className="rounded-2xl border border-brand-coral/25 bg-wash-coral p-4 text-sm font-bold text-ink">
              {error}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
